// @flow

import * as React from 'react';
import glamorous from 'glamorous';
import { position } from 'polished';
import { cond, eq, stubTrue } from 'lodash/fp';
import t from 'app/utils/translate';
import ucDesktopImage from 'app/images/globalLeaderboard/ultimate-champion-desktop.png';
import ucMobileImage from 'app/images/globalLeaderboard/ultimate-champion-mobile.png';
import hofDesktopImage from 'app/images/globalLeaderboard/hall-of-fame-desktop.png';
import hofMobileImage from 'app/images/globalLeaderboard/hall-of-fame-mobile.png';
import ucDesktopImageChina from 'app/images/globalLeaderboard/ultimate-champion-desktop-cn.png';
import ucMobileImageChina from 'app/images/globalLeaderboard/ultimate-champion-mobile-cn.png';
import ucDesktopImageEPL from 'app/images/globalLeaderboard/ultimate-champion-desktop-epl.jpg';
import ucMobileImageEPL from 'app/images/globalLeaderboard/ultimate-champion-mobile-epl.jpg';
import ucDesktopImageWorldCup from 'app/images/globalLeaderboard/ultimate-champion-desktop-world-cup.jpg';
import ucMobileImageWorldCup from 'app/images/globalLeaderboard/ultimate-champion-mobile-world-cup.jpg';
import hofDesktopImageChina from 'app/images/globalLeaderboard/hall-of-fame-desktop-cn.png';
import hofMobileImageChina from 'app/images/globalLeaderboard/hall-of-fame-mobile-cn.png';
import LightTab from 'app/components/commons/LightTab';
import LightTabSet from 'app/components/commons/LightTabSet';
import MenuWrapper from 'app/components/commons/MenuWrapper';
import ResponsiveBanner from 'app/components/commons/ResponsiveBanner';
import UltimateChampionContent from './UltimateChampionContent';
import HallOfFameContent from './HallOfFameContent';
import EnglishPremierLeagueHowToPlay from './EnglishPremierLeagueHowToPlay';
import WorldCupHowToPlay from './WorldCupHowToPlay';

type Props = {
  competitionSlug: string,
  leaderboardName: string,
  locale: string,
  spartan: boolean,
  sportsList: Array<Object>,
  sportSlug: string,
};

function getDesktopImage(locale, section) {
  switch (locale) {
    case 'cn':
      return {
        leaderboard: ucDesktopImageChina,
        'hall-of-fame': hofDesktopImageChina,
      }[section];
    default:
      return {
        leaderboard: ucDesktopImage,
        'hall-of-fame': hofDesktopImage,
      }[section];
  }
}

function getMobileImage(locale, section) {
  switch (locale) {
    case 'cn':
      return {
        leaderboard: ucMobileImageChina,
        'hall-of-fame': hofMobileImageChina,
      }[section];
    default:
      return {
        leaderboard: ucMobileImage,
        'hall-of-fame': hofMobileImage,
      }[section];
  }
}

const SponsorWrapper = glamorous.div(({ theme }) => ({
  border: '1px solid rgba(255, 255, 255, 0.3)',
  paddingTop: '30%',
  position: 'relative',
  [theme.mediaQuery.desktopAndUp]: {
    paddingTop: '7.5%',
  },
}));

const SponsorImage = glamorous.div(
  ({ theme }) => ({
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% auto',
    ...position('absolute', 0, 0, '-200%'),
    zIndex: -10,
    [theme.mediaQuery.desktopAndUp]: {
      zIndex: 0,
    },
  }),
  ({ leaderboardName, locale, theme }) => ({
    backgroundImage: `url(${getMobileImage(locale, leaderboardName)})`,
    [theme.mediaQuery.desktopAndUp]: {
      backgroundImage: `url(${getDesktopImage(locale, leaderboardName)})`,
    },
  }),
);

const Tabs = glamorous(LightTabSet)(
  {
    display: 'flex',
    paddingTop: 2,
  },
  ({ theme }) => ({
    marginBottom: theme.spacing.default,
  }),
);

function hasHowToPlay(competitionSlug) {
  return competitionSlug === 'epl';
}

function hasCustomBanner(competitionSlug) {
  return competitionSlug === 'wc' || competitionSlug === 'epl';
}

function GlobalLeaderboardContent({
  competitionSlug,
  sportSlug,
  leaderboardName,
  locale,
  sportsList,
  spartan,
}: Props) {
  const sport = sportsList.find(s => s.slug === sportSlug);
  const competition = sport.subcategories.find(c => c.slug === competitionSlug);

  if (!competition) {
    return null;
  }

  return (
    <div>
      <MenuWrapper
        key={competitionSlug}
        component={Tabs}
        value={leaderboardName}
        spartan={spartan}
      >
        {hasHowToPlay(competitionSlug) && (
          <LightTab
            id="how-to-play"
            to={`/ultimate-champion/how-to-play/${sportSlug}/${competitionSlug}`}
          >
            {t('How to Play')}
          </LightTab>
        )}
        <LightTab
          id="leaderboard"
          to={`/ultimate-champion/leaderboard/${sportSlug}/${competitionSlug}`}
        >
          {t('Leaderboard')}
        </LightTab>
        <LightTab
          id="hall-of-fame"
          to={`/ultimate-champion/hall-of-fame/${sportSlug}/${competitionSlug}`}
        >
          {t('Hall of Fame')}
        </LightTab>
      </MenuWrapper>
      {leaderboardName === 'leaderboard' && (
        cond([
          [eq('wc'), () => (
            <ResponsiveBanner
              desktopImage={ucDesktopImageWorldCup}
              mobileImage={ucMobileImageWorldCup}
            />
          )],
          [eq('epl'), () => (
            <ResponsiveBanner
              desktopImage={ucDesktopImageEPL}
              mobileImage={ucMobileImageEPL}
            />
          )],
          [stubTrue, () => (
            <SponsorWrapper>
              <SponsorImage leaderboardName={leaderboardName} locale={locale} />
            </SponsorWrapper>
          )],
        ])(competitionSlug)
      )}
      {leaderboardName === 'hall-of-fame' && (
        <SponsorWrapper>
          <SponsorImage leaderboardName={leaderboardName} locale={locale} />
        </SponsorWrapper>
      )}
      {leaderboardName === 'leaderboard' && (
        <UltimateChampionContent
          competitionId={competition.id}
          spartan={spartan}
          hasCustomBanner={hasCustomBanner(competitionSlug)}
        />
      )}
      {leaderboardName === 'hall-of-fame' && (
        <HallOfFameContent competitionId={competition.id} />
      )}
      {leaderboardName === 'how-to-play' && (
        <React.Fragment>
          {competitionSlug === 'wc' && <WorldCupHowToPlay />}
          {competitionSlug === 'epl' && <EnglishPremierLeagueHowToPlay />}
        </React.Fragment>
      )}
    </div>
  );
}

export default GlobalLeaderboardContent;
